import { TextField } from "@mui/material";
import moment from "moment";
import React, { useEffect, useState, useCallback, useMemo } from "react";
import { DayPicker } from "react-day-picker";
import "react-day-picker/dist/style.css";
import classes from "./index.module.scss";
import { DEFAULT_TIMEZONE } from "../../../Helpers/Constants";
import { Tooltip as ReactTooltip } from "react-tooltip";
import "react-tooltip/dist/react-tooltip.css";
import { HiChevronRight, HiChevronLeft } from "react-icons/hi2";

const DatePickerWithInput = ({
  onChange,
  value,
  blockDates,
  tooltipDays,
  disabled,
  datePickerclass,
  showDateclass,
  orderTimezone,
}) => {
  // Memoize the renderDay function to prevent unnecessary recalculations
  const renderDay = useCallback((day, tooltipDay, selectedDate, disableDay) => {
    const date = day.getDate();
    const month = day.getMonth();
    const year = day.getFullYear();
    
    // Memoize these calculations
    const selectedDateFromString = selectedDate ? new Date(selectedDate).getDate() : null;
    const selectedDateMonth = selectedDate ? new Date(selectedDate).getMonth() : null;

    let tooltp = tooltipDay?.find(
      (d) => d.date === date && d.month === month && d.year === year
    );
    const dateStyle = {
      bottom: 0,
      right: 0,
      fontSize: "13px",
    };

    // Move isDateDisabled logic inline (no hook needed)
    const isDateDisabled = (date, disableDay) => {
      if (!disableDay) return false;

      const currentDate = new Date(date).setHours(0, 0, 0, 0);

      return disableDay.some((rule) => {
        if (rule.before) {
          const beforeDate = new Date(rule.before).setHours(0, 0, 0, 0);
          return currentDate < beforeDate;
        }
        if (rule.from && rule.to) {
          const fromDate = new Date(rule.from).setHours(0, 0, 0, 0);
          const toDate = new Date(rule.to).setHours(0, 0, 0, 0);
          return currentDate >= fromDate && currentDate <= toDate;
        }
        if (rule instanceof Date) {
          return currentDate === new Date(rule).setHours(0, 0, 0, 0);
        }
        return false;
      });
    };

    const isDisabled =
      new Date(day).setHours(0, 0, 0, 0) < new Date().setHours(0, 0, 0, 0) ||
      isDateDisabled(day, disableDay);
      
    const isSelected =
      selectedDateFromString === date && selectedDateMonth === month;

    // Check if this is today's date
    const today = new Date();
    const isToday = 
      day.getDate() === today.getDate() &&
      day.getMonth() === today.getMonth() &&
      day.getFullYear() === today.getFullYear();

    let buttonClass = "rdp_day_button";

    if (tooltp) {
      buttonClass = isDisabled
        ? "rdp_day_button_toolttip_disabled"
        : "rdp_day_button_toolttip";
    } else if (isDisabled) {
      buttonClass = "rdp_day_button_disabled";
    }

    if (isSelected && !isDisabled) {
      buttonClass = "rdp_day_button_selected";
    }

    // Add today class if this is today's date and not disabled
    if (isToday && !isDisabled) {
      buttonClass += " rdp_day_button_today";
    }

    // Move click handler inline (no hook needed)
    const handleClick = () => {
      if (!isDisabled) {
        onChange(day);
        setShowPicker(false);
      }
    };

    return (
      <>
        {tooltp?.date === date &&
        month === tooltp?.month &&
        year === tooltp?.year ? (
          <>
            <td
              className="rdp-day"
              data-day={day.toISOString()}
              role="gridcell"
              data-tooltip-id={tooltp?.id}
            >
              <button
                onClick={handleClick}
                className={buttonClass}
                style={dateStyle}
                type="button"
                tabIndex={-1}
              >
                {date}
              </button>
              <ReactTooltip
                id={tooltp?.id}
                place="top"
                content={tooltp?.message}
                className="tooltip tooltipNewDesign"
                variant="dark"
              />
            </td>
          </>
        ) : (
          <td
            className="rdp-day"
            data-day={day.toISOString()}
            role="gridcell"
          >
            <button
              onClick={handleClick}
              className={buttonClass}
              style={dateStyle}
              type="button"
              tabIndex={-1}
            >
              {date}
            </button>
          </td>
        )}
      </>
    );
  }, [onChange]);

  // Memoize disableAllDays to prevent recalculation on every render
  const disableAllDays = useMemo(() => [
    {
      before: new Date(
        new Date().toLocaleString("en-US", {
          timeZone: orderTimezone || DEFAULT_TIMEZONE,
        })
      ),
    },
    ...(blockDates ?? []),
  ], [orderTimezone, blockDates]);

  const [showPicker, setShowPicker] = useState(false);
  const [insidePicker, setInsidePicker] = useState(false);
  const [inputFocused, setInputFocused] = useState(false);

  // Memoize the Day component to prevent unnecessary re-renders
  const DayComponent = useCallback(({ day, modifiers, ...dayProps }) =>
    renderDay(day.date, tooltipDays, value, disableAllDays), 
    [renderDay, tooltipDays, value, disableAllDays]
  );

  // Memoize the Chevron component to prevent unnecessary re-renders
  const ChevronComponent = useCallback(({ orientation, ...props }) => (
    <div>
      {orientation === "left" ? (
        <div className={"rdp_left_chevron_icon"}>
          <HiChevronLeft strokeWidth={0} color="#000" size={18} />
        </div>
      ) : (
        <div className={"rdp_right_chevron_icon"}>
          <HiChevronRight strokeWidth={0} color="#000" size={18} />
        </div>
      )}
    </div>
  ), []);

  useEffect(() => {
    // Only add event listeners when the picker is shown
    if (!showPicker) return;

    const dp = document.querySelector(".rdp-root");
    if (dp) {
      const handleMouseEnter = () => setInsidePicker(true);
      const handleMouseLeave = () => setInsidePicker(false);

      dp.addEventListener("mouseenter", handleMouseEnter);
      dp.addEventListener("mouseleave", handleMouseLeave);

      return () => {
        dp.removeEventListener("mouseenter", handleMouseEnter);
        dp.removeEventListener("mouseleave", handleMouseLeave);
      };
    }
  }, [showPicker]); // Add showPicker as dependency

  useEffect(() => {
    if (inputFocused || insidePicker) {
      setShowPicker(true);
    } else {
      setShowPicker(false);
    }
    return () => {};
  }, [insidePicker, inputFocused]);

  return (
    <>
      <TextField
        className="w-100"
        variant="standard"
        disabled={disabled}
        onBlur={() => {
          setInputFocused(false);
        }}
        onFocus={() => {
          setInputFocused(true);
        }}
        onChange={(e) => {
          return;
        }}
        value={value ? moment(value).format("MM-DD-YYYY") : ""}
        type="text"
        label="Delivery Date"
      />

      <DayPicker
        className={`${"rdpRoot"} ${showPicker ? "" : "d-none"} ${
          showDateclass ? datePickerclass : ""
        }`}
        showOutsideDays
        navLayout="around"
        selected={value}
        disabled={disableAllDays}
        components={{
          Day: DayComponent,
          MonthCaption: ({ children, ...props }) => (
            <div className={"rdp_month_caption"}>{children}</div>
          ),
          Chevron: ChevronComponent,
        }}
      />
    </>
  );
};

export default DatePickerWithInput;
